import { nothing } from 'lit-html';
import { html, property } from 'lit-element';
import {
  KatLitElement,
  Keys,
  register,
  event,
  EventEmitter,
} from '../../shared/base';

import baseStyles from '../../shared/base/base.lit.scss';
import styles from './selection-list.lit.scss';

/**
 * @component {kat-selection-list} KatalSelectionList A control to allow users to choose between a vertical list of items.
 * @slot title Contents will be used as the selection list title.
 * @slot default Default slot for list items - must use kat-selection-list-item to work properly
 * @example Basic {"title": "Basic", "items":[ { "name":"First item", "value":"1" }, { "name":"Second item", "value":"2" }, { "name":"Third item", "value":"3" } ]}
 * @example Slots { "content": "<span slot=\"title\">Title</span><kat-selection-list-item value=\"basic\">Basic Item</kat-selection-list-item><kat-selection-list-item value=\"emoji\"><span>Emoji Item 😎</span></kat-selection-list-item><kat-selection-list-item><p>Selection list item with a badge!</p><kat-badge label=\"a kat-badge\" type=\"default\" variant=\"angled\"></kat-badge></kat-selection-list-item>"}
 * @status Production
 * @subcomponent ./selection-list-item.ts
 */
@register('kat-selection-list')
export class KatSelectionList extends KatLitElement {
  /**
   * The text to display for the selection list
   * @required false
   */
  @property()
  title: string;

  /** Value of currently-selected list item */
  @property()
  selected?: string;

  /**
   * An array of list item objects to be displayed.
   * Items must be objects with name and value properties, e.g.: { name: "name", value: "value"}.
   * Attribute: requires stringified JSON
   * Property: requires an array
   */
  @property()
  items?: { name: string; value: string }[];

  /** Fires when one of the categories have been selected. */
  @event('change', true)
  private _change: EventEmitter<{ selected: string }>;

  static get styles() {
    return [baseStyles, styles];
  }

  _handleClick(event) {
    const selectedItem = event.target.closest('kat-selection-list-item');
    if (
      !selectedItem ||
      !selectedItem.value ||
      (this.selected && selectedItem.value === this.selected)
    ) {
      return;
    }

    this.select(selectedItem.value);
  }

  /**
   * Call to toggle the "selected" kat-selection-list-item with a provided value.
   * Fires the 'change' event.
   *
   * @katalmethod
   */
  async select(value) {
    if (this.selected === value) return;
    this.selected = value;

    await this.updateComplete;
    this._change.emit({ selected: this.selected });
  }

  firstUpdated() {
    this.addEventListener('keydown', e => this._handleKeyDown(e), {
      capture: true,
      passive: true,
    });
  }

  _handleKeyDown(event) {
    if (
      (event.keyCode !== Keys.Enter && event.keyCode !== Keys.Space) ||
      !event.composedPath().some(c => c.localName === 'kat-selection-list-item')
    ) {
      return;
    }
    const selectedItem = event
      .composedPath()
      .find(c => c.localName === 'kat-selection-list-item');
    if (selectedItem && this.selected !== selectedItem.value) {
      this.select(selectedItem.value);
    }
  }

  _handleSelection() {
    const item = this.querySelector(
      `kat-selection-list-item[value="${this.selected}"]`
    );
    if (!item) return;
    if (this._selectedElement) this._selectedElement.selected = false;
    item.selected = true;
    this._selectedElement = item;
    this.requestUpdate();
  }

  _childrenChanged() {
    this.requestUpdate();
  }

  updated(values) {
    if (values.has('selected')) {
      this._handleSelection();
    }
  }

  render() {
    return html`
      <div class="title" part="selection-list-title">
        <slot name="title">${this.title || nothing}</slot>
      </div>
      <div class="list" @click=${this._handleClick} part="selection-list-items">
        <slot @slotchange=${this._childrenChanged}></slot>
        ${Array.isArray(this.items)
          ? this.items.map(
              item =>
                html`
                  <kat-selection-list-item
                    part="selection-list-item"
                    class="item"
                    .value=${item.value}
                    ?selected=${this.selected === item.value}
                    >${item.name}</kat-selection-list-item
                  >
                `
            )
          : nothing}
      </div>
    `;
  }
}
